import ExcelJS from "exceljs";
import ColorLogo from "../assets/login/ColorLogo.png";
import logo1 from "../assets/header/HABD.png";
import logo2 from "../assets/login/DMW_Patiala.png";

// Helper functions for formatting (same as in LogSearch component)
const formatDate = (dateString) => {
  if (!dateString) return '';
  
  const date = new Date(dateString);
  const day = date.getDate();
  const month = date.toLocaleString('default', { month: 'short' });
  const year = date.getFullYear();
  
  // Format time as "12:38 PM"
  let hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12; // Convert to 12-hour format
  
  return `${day} ${month} ${year} ${hours}:${minutes} ${ampm}`;
};

// Calculate browsing time from login and logout time
const calculateBrowsingTime = (loginTime, logoutTime) => {
  if (!logoutTime) {
    return "Active";
  }
  
  const login = new Date(loginTime);
  const logout = new Date(logoutTime);
  const diffMs = logout - login;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHrs = Math.floor(diffMins / 60);
  const remainingMins = diffMins % 60;
  
  return `${diffHrs}h ${remainingMins}m`;
};

const DownloadUserLogsExcel = async (
  selectedSite,
  selectedSiteName,
  userLogs,
  timePeriod,
  setIsLoading
) => {
  try {
    setIsLoading(true);
    
    const user = JSON.parse(localStorage.getItem("user"));
    const userName = user?.username || "Guest";
    const downloadDate = new Date();
    const formattedDateTime = formatDate(downloadDate); // Using the same format as table
    
    // Calculate date range for time period
    const toDate = new Date();
    const fromDate = new Date();
    fromDate.setDate(toDate.getDate() - parseInt(timePeriod));
    
    const formattedFromDate = formatDate(fromDate);
    const formattedToDate = formatDate(toDate);
    
    // Create workbook and worksheet
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("User Logs");
    
    try {
      // Fetch images
      const colorLogoBuffer = await fetch(ColorLogo).then((res) => {
        if (!res.ok) throw new Error("Failed to fetch ColorLogo.png");
        return res.arrayBuffer();
      });
      const logo1Buffer = await fetch(logo1).then((res) => {
        if (!res.ok) throw new Error("Failed to fetch HABD.png");
        return res.arrayBuffer();
      });
      const logo2Buffer = await fetch(logo2).then((res) => {
        if (!res.ok) throw new Error("Failed to fetch DMW_Patiala.png");
        return res.arrayBuffer();
      });
      
      // Add images to workbook
      const colorLogoId = workbook.addImage({
        buffer: colorLogoBuffer,
        extension: "png",
      });
      const logo1Id = workbook.addImage({
        buffer: logo1Buffer,
        extension: "png",
      });
      const logo2Id = workbook.addImage({
        buffer: logo2Buffer,
        extension: "png",
      });
      
      // Merge cells for logos and title (adjusted for 8-column table)
      worksheet.mergeCells("A1:B1"); // ColorLogo area
      worksheet.mergeCells("C1:F1"); // Logo1 + Title area
      worksheet.mergeCells("G1:H1"); // Logo2 area
      
      // Add ColorLogo centered in A1:B1
      worksheet.addImage(colorLogoId, {
        tl: { col: 0.5, row: 0.5 },
        ext: { width: 120, height: 60 },
      });
      worksheet.getCell("A1").alignment = {
        horizontal: "center",
        vertical: "middle",
      };
      
      // Add Logo1 with slight offset in C1:F1 to allow gap before title
      worksheet.addImage(logo1Id, {
        tl: { col: 2.2, row: 0.5 }, // Slight left offset inside merged range
        ext: { width: 60, height: 60 },
      });
      
      // Add title with proper spacing after logo1
      const title = "NOVIUS HABD (Lite) - NOVIUS HOT AXLE BOX DETECTION SYSTEM (LITE MODEL)";
      worksheet.getCell("C1").value = title;
      worksheet.getCell("C1").font = {
        bold: true,
        size: 14,
        name: "Helvetica",
      };
      worksheet.getCell("C1").alignment = {
        horizontal: "left", // Left align so it appears after logo
        vertical: "middle",
        indent: 8, // Adds spacing between logo and text
      };
      
      // Add Logo2 centered in G1:H1
      worksheet.addImage(logo2Id, {
        tl: { col: 6.5, row: 0.5 },
        ext: { width: 80, height: 60 },
      });
      worksheet.getCell("G1").alignment = {
        horizontal: "center",
        vertical: "middle",
      };
      
      // Set height for header row
      worksheet.getRow(1).height = 65;
      // console.log("All logos added successfully to header");
    } catch (error) {
      console.error("Error loading logo images:", error);
      
      // Fallback layout without logos
      worksheet.mergeCells("A1:B1");
      worksheet.mergeCells("C1:F1");
      worksheet.mergeCells("G1:H1");
      worksheet.getCell("A1").value = "NOVIUS";
      worksheet.getCell("A1").font = { bold: true, size: 14, color: { argb: "FF0066CC" } };
      worksheet.getCell("A1").alignment = { horizontal: "center", vertical: "middle" };
      worksheet.getCell("C1").value = "NOVIUS HABD (Lite) - NOVIUS HOT AXLE BOX DETECTION SYSTEM (LITE MODEL)";
      worksheet.getCell("C1").font = { bold: true, size: 14, name: "Helvetica" };
      worksheet.getCell("C1").alignment = { horizontal: "center", vertical: "middle" };
      worksheet.getCell("G1").value = "DMW Patiala";
      worksheet.getCell("G1").font = { bold: true, size: 12 };
      worksheet.getCell("G1").alignment = { horizontal: "center", vertical: "middle" };
      worksheet.getRow(1).height = 65;
    }
    
    // Footer: "Downloaded by" and "Download Time" (Row 2)
    worksheet.getCell("A2").value = `Downloaded by: ${userName}`;
    worksheet.getCell("A2").font = {
      bold: true,
      size: 10,
      name: "Helvetica",
      italic: true,
    };
    worksheet.getCell("A2").alignment = {
      horizontal: "left",
      vertical: "middle",
    };
    
    // Add "Download Time" on the right (H2)
    worksheet.getCell("H2").value = `Download Time: ${formattedDateTime}`;
    worksheet.getCell("H2").font = {
      bold: true,
      size: 10,
      name: "Helvetica",
      italic: true,
    };
    worksheet.getCell("H2").alignment = {
      horizontal: "right",
      vertical: "middle",
    };
    worksheet.getRow(2).height = 20;
    
    // Add Site and Date Range (Row 3 and 4)
    const siteRow = worksheet.addRow([]);
    siteRow.getCell(1).value = "Site:";
    siteRow.getCell(1).font = { bold: true, size: 11, name: "Calibri" };
    siteRow.getCell(2).value = selectedSiteName || "N/A";
    siteRow.getCell(2).font = { size: 11, name: "Calibri" };
    
    const dateRow = worksheet.addRow([]);
    dateRow.getCell(1).value = "Time Period:";
    dateRow.getCell(1).font = { bold: true, size: 11, name: "Calibri" };
    dateRow.getCell(2).value = `${formattedFromDate} to ${formattedToDate}`;
    dateRow.getCell(2).font = { size: 11, name: "Calibri" };
    
    const generatedRow = worksheet.addRow([]);
    generatedRow.getCell(1).value = "Generated on:";
    generatedRow.getCell(1).font = { bold: true, size: 11, name: "Calibri" };
    generatedRow.getCell(2).value = formattedDateTime;
    generatedRow.getCell(2).font = { size: 11, name: "Calibri" };
    
    worksheet.addRow([]); // Spacer
    
    const overviewRow = worksheet.addRow([]);
    overviewRow.getCell(1).value = "User Logs";
    overviewRow.getCell(1).font = { bold: true, size: 14, name: "Calibri" };
    
    // Table headers - no spacer row before headers
    const headers = [
      "Sr. No.",
      "User",
      "Email",
      "Login Time",
      "Logout Time",
      "Browsing Time",
      "Status",
      "IP Address",
    ];
    
    const headerRow = worksheet.addRow(headers);
    
    headerRow.eachCell((cell, colNumber) => {
      cell.font = { bold: true, size: 12, name: "Helvetica" };
      cell.alignment = { horizontal: "center", vertical: "middle" };
      // Lighter background color for header
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "E6E6E6" }, // Lighter gray background
      };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });
    headerRow.height = 25;
    
    // Add user logs data immediately after header
    if (userLogs?.length > 0) {
      userLogs.forEach((log, index) => {
        const row = worksheet.addRow([
          index + 1,
          log.username || "N/A",
          log.email || "N/A",
          log.LoginTime ? formatDate(log.LoginTime) : "N/A",
          log.LogoutTime ? formatDate(log.LogoutTime) : "Still Active",
          calculateBrowsingTime(log.LoginTime, log.LogoutTime),
          log.IsActive ? "Active" : "Inactive",
          log.IPAddress || "N/A",
        ]);
        
        row.eachCell((cell, colNumber) => {
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
          
          // Set alignment based on column
          cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
          
          // Highlight status column based on value
          if (colNumber === 7) { // Status column
            const status = cell.value?.toString().toLowerCase();
            if (status === "active") {
              cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "90EE90" }, // Light green background
              };
            } else if (status === "inactive") {
              cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "E6E6E6" }, // Light gray background
              };
            }
          }
        });
      });
    } else {
      const noDataRow = worksheet.addRow(["No user logs data available"]);
      noDataRow.getCell(1).font = { italic: true, size: 11 };
      noDataRow.getCell(1).alignment = { horizontal: "center" };
      worksheet.mergeCells(`A${noDataRow.number}:H${noDataRow.number}`); // Merge across all columns
    }
    
    // Set column widths based on content
    worksheet.columns = [
      { width: 8 },   // Sr. No.
      { width: 15 },  // User
      { width: 25 },  // Email
      { width: 20 },  // Login Time
      { width: 20 },  // Logout Time
      { width: 15 },  // Browsing Time
      { width: 15 },  // Status
      { width: 20 },  // IP Address
    ];
    
    // Generate and download Excel file
    const excelFileName = `User_Logs_${selectedSiteName}_${new Date().toISOString().slice(0, 10)}.xlsx`;
    
    // console.log("Generating Excel buffer...");
    const buffer = await workbook.xlsx.writeBuffer();
    // console.log("Excel buffer generated, size:", buffer.byteLength, "bytes");
    
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = excelFileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    // console.log("Excel file downloaded successfully");
  } catch (error) {
    console.error("Error generating Excel:", error);
    alert(`Failed to generate Excel: ${error.message}`);
  } finally {
    setIsLoading(false);
  }
};

export default DownloadUserLogsExcel;